import LineLuxuryEscapesMonogramIcon from 'LuxKit/Icons/line/LineLuxuryEscapesMonogramIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import clsx from 'clsx'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { rem } from 'polished'
import type { ComponentProps, PropsWithChildren, ReactElement } from 'react'
import styled from 'styled-components'

const Icon = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  display: grid;
  place-items: center;
  border-radius: ${props => props.theme.borderRadius.round};

  &.density-compact {
    height: ${rem(24)};
    width: ${rem(24)};
    ${setSvgIconSize(16)}
  }
  &.density-comfortable {
    height: ${rem(32)};
    width: ${rem(32)};
    ${setSvgIconSize(20)}
  }

  &:not(.flat) {
    &.tier-bronze {
      background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
    }
    &.tier-silver {
      background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
    }
    &.tier-gold {
      background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
    }
    &.tier-platinum {
      background-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.lightContrast};
    }
  }

  &.flat {
    &.tier-bronze {
      background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
    }
    &.tier-silver {
      background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
    }
    &.tier-gold {
      background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
    }
    &.tier-platinum {
      background-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.lightContrast};
    }
  }
`

const Content = styled(BodyText)`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
`

const EndContent = styled.div`
  flex-shrink: 0;
  display: flex;

  &:empty { display: none; }
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};

  &.density-compact {
    min-height: ${rem(40)};
    padding-block: ${rem(8)};
    padding-inline: ${rem(8)};

    &:not(:has(${EndContent} input), :has(${EndContent} button)) {
      padding-inline-end: ${rem(12)};
    }
  }
  &.density-comfortable {
    min-height: ${rem(56)};
    padding-block: ${rem(8)};
    padding-inline: ${rem(12)};

    &:not(:has(${EndContent} input), :has(${EndContent} button)) {
      padding-inline-end: ${rem(20)};
    }
  }

  &:not(.flat) {
    &.tier-bronze {
      color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
      background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
    }
    &.tier-silver {
      color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
      background: ${props => props.theme.palette.product.loyalty.silver.gradientSecondaryBackground};
    }
    &.tier-gold {
      color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
      background: ${props => props.theme.palette.product.loyalty.gold.gradientSecondaryBackground};
    }
    &.tier-platinum {
      color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
      background: ${props => props.theme.palette.product.loyalty.platinum.gradientSecondaryBackground};
    }
  }

  &.flat {
    &.tier-bronze {
      background: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    }
    &.tier-silver {
      background: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    }
    &.tier-gold {
      background: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    }
    &.tier-platinum {
      background: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
    }
  }
`

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  tier: App.LoyaltyTier
  /**
   * @default comfortable
   */
  density?: 'comfortable' | 'compact'
  /**
   * @default Brand logo
   */
  icon?: ReactElement | false
  endContent?: ReactElement | false
  /** flat background over gradient */
  flat?: boolean
  className?: string
}

const SIZES_BODY_TEXT_VARIANTS: Record<Exclude<Props['density'], undefined>, ComponentProps<typeof BodyText>['variant']> = {
  comfortable: 'large',
  compact: 'medium',
}

function LoyaltyTierMessageBanner({
  tier,
  density = 'comfortable',
  icon = <LineLuxuryEscapesMonogramIcon />,
  endContent,
  children,
  flat,
  className,
  ...rest
}: Props) {
  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      {...rest}
      className={clsx(`tier-${tier}`,
        `density-${density}`,
        className,
        { flat },
      )}
    >
      {!!icon && <Icon className={clsx(`tier-${tier}`, `density-${density}`, { flat })}>
        {icon}
      </Icon>}
      <Content variant={SIZES_BODY_TEXT_VARIANTS[density]}>
        <div>{children}</div>
        {!!endContent && <EndContent>{endContent}</EndContent>}
      </Content>
    </Container>
  </ProductPaletteProvider>
}

export default LoyaltyTierMessageBanner
